import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tpAPI } from '../../services/tournamentPlayerAPI';
import { groupStageAPI } from '../../services/groupStageAPI';
import { Modal, MatchUpdateForm } from '../../components/common';
import './GroupManager.css';

// ─── tiny helpers ──────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2);

const STEPS = [
  { id: 'groups',   label: 'Groups',   icon: '📁' },
  { id: 'teams',    label: 'Teams',    icon: '🏅' },
  { id: 'players',  label: 'Players',  icon: '👤' },
  { id: 'matches',  label: 'Matches',  icon: '🎾' },
  { id: 'standings',label: 'Standings',icon: '📊' },
];

// ─── Inline edit input ─────────────────────────────────────────────────────────
function Editable({ value, onSave, placeholder, bold }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const commit = () => { if (val.trim() && val !== value) onSave(val.trim()); setEditing(false); };
  if (!editing) return (
    <span
      className={`gm-editable${bold ? ' gm-editable-bold' : ''}`}
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {value || <span className="gm-placeholder">{placeholder}</span>}
      <span className="gm-edit-icon">✏️</span>
    </span>
  );
  return (
    <input
      autoFocus
      className="gm-inline-input"
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      placeholder={placeholder}
    />
  );
}

// ─── Confirm dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ msg, onConfirm, onCancel }) {
  return (
    <div className="gm-overlay" onClick={onCancel}>
      <div className="gm-dialog" onClick={e => e.stopPropagation()}>
        <p>{msg}</p>
        <div className="gm-dialog-btns">
          <button className="gm-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="gm-btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Score entry modal ─────────────────────────────────────────────────────────
// Uses shared MatchUpdateForm component (same as normal matches)

// ─── STEP 1: Groups ────────────────────────────────────────────────────────────
function StepGroups({ groups, groupStage, onAdd, onRename, onDelete, onNext }) {
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState(null);

  const letters = ['A','B','C','D','E','F','G','H'];
  const nextLetter = letters[groups.length] || (groups.length + 1);

  const submit = async () => {
    if (!name.trim()) return;
    await onAdd(name.trim());
    setName('');
  };

  return (
    <div className="gm-step">
      <div className="gm-step-desc">
        Create the groups for this tournament — e.g. Group A, Group B. Each group will have its own teams and round-robin matches.
      </div>

      {/* Quick-add */}
      <div className="gm-add-bar">
        <input
          className="gm-input"
          placeholder={`e.g. Group ${nextLetter}`}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
        />
        <button className="gm-btn-primary" onClick={submit}>+ Add Group</button>
      </div>

      {/* Quick-add multiple */}
      <div className="gm-quick-row">
        <span className="gm-hint">Quick add:</span>
        {letters.slice(groups.length, groups.length + 4).map(l => (
          <button key={l} className="gm-quick-btn" onClick={() => onAdd(`Group ${l}`)}>
            Group {l}
          </button>
        ))}
      </div>

      {/* List */}
      {groups.length === 0
        ? <div className="gm-empty">No groups yet. Add your first group above.</div>
        : (
          <div className="gm-list">
            {groups.map((g, idx) => (
              <div key={g._id} className="gm-list-row">
                <span className="gm-list-num">{idx + 1}</span>
                <span className="gm-folder-icon">📁</span>
                <Editable
                  value={g.name}
                  placeholder="Group name"
                  bold
                  onSave={v => onRename(g._id, v)}
                />
                <span className="gm-meta">{(g.teamIds?.length || 0)} teams</span>
                <button className="gm-del-btn" onClick={() => setConfirm(g)}>Delete</button>
              </div>
            ))}
          </div>
        )}

      {groups.length > 0 &&
        <div className="gm-step-footer">
          <button className="gm-btn-next" onClick={onNext}>
            Next: Add Teams →
          </button>
        </div>}

      {confirm && <ConfirmDialog
        msg={`Delete "${confirm.name}"? All teams and matches inside will also be deleted.`}
        onConfirm={() => { onDelete(confirm._id); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />}
    </div>
  );
}

// ─── STEP 2: Teams ─────────────────────────────────────────────────────────────
function StepTeams({ groups, teamsMap, onAddTeam, onRenameTeam, onDeleteTeam, onNext, onPrev }) {
  const [activeGroup, setActiveGroup] = useState(groups[0]?._id || '');
  const [form, setForm] = useState({ teamName: '', teamCode: '' });
  const [confirm, setConfirm] = useState(null);

  const teams = teamsMap[activeGroup] || [];

  const submit = async () => {
    if (!form.teamName.trim() || !form.teamCode.trim()) return;
    await onAddTeam(activeGroup, form);
    setForm({ teamName: '', teamCode: '' });
  };

  return (
    <div className="gm-step">
      <div className="gm-step-desc">
        Add teams to each group. Each team competes only within its group (like ICC Cricket World Cup pools — but for Tennis).
      </div>

      {/* Group selector */}
      <div className="gm-group-tabs">
        {groups.map(g => (
          <button
            key={g._id}
            className={`gm-group-tab${activeGroup === g._id ? ' active' : ''}`}
            onClick={() => setActiveGroup(g._id)}
          >
            {g.name}
            <span className="gm-tab-count">{(teamsMap[g._id] || []).length}</span>
          </button>
        ))}
      </div>

      {/* Add team */}
      <div className="gm-add-bar">
        <input
          className="gm-input gm-input-flex"
          placeholder="Team name (e.g. India)"
          value={form.teamName}
          onChange={e => setForm(f => ({ ...f, teamName: e.target.value }))}
          onKeyDown={e => e.key === 'Enter' && submit()}
        />
        <input
          className="gm-input gm-input-code"
          placeholder="Code"
          maxLength={4}
          value={form.teamCode}
          onChange={e => setForm(f => ({ ...f, teamCode: e.target.value.toUpperCase() }))}
          onKeyDown={e => e.key === 'Enter' && submit()}
        />
        <button className="gm-btn-primary" onClick={submit}>+ Add Team</button>
      </div>

      {/* Team list */}
      {teams.length === 0
        ? <div className="gm-empty">No teams in this group yet.</div>
        : (
          <div className="gm-list">
            {teams.map((t, idx) => (
              <div key={t._id} className="gm-list-row">
                <span className="gm-list-num">{idx + 1}</span>
                <span className="gm-code-badge">{t.teamCode}</span>
                <Editable value={t.teamName} placeholder="Team name" bold
                  onSave={v => onRenameTeam(t._id, { teamName: v, teamCode: t.teamCode })} />
                <span className="gm-meta">{(t.playerIds?.length || 0)} players</span>
                <button className="gm-del-btn" onClick={() => setConfirm(t)}>Delete</button>
              </div>
            ))}
          </div>
        )}

      <div className="gm-step-footer">
        <button className="gm-btn-ghost" onClick={onPrev}>← Back</button>
        {Object.values(teamsMap).flat().length > 0 &&
          <button className="gm-btn-next" onClick={onNext}>Next: Add Players →</button>}
      </div>

      {confirm && <ConfirmDialog
        msg={`Delete team "${confirm.teamName}"?`}
        onConfirm={() => { onDeleteTeam(confirm._id); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />}
    </div>
  );
}

// ─── STEP 3: Players ───────────────────────────────────────────────────────────
function StepPlayers({ groups, teamsMap, players, onAddPlayer, onDeletePlayer, onNext, onPrev }) {
  const [activeGroup, setActiveGroup] = useState(groups[0]?._id || '');
  const [activeTeam, setActiveTeam] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  const teams = teamsMap[activeGroup] || [];

  useEffect(() => {
    setActiveTeam(teams[0]?._id || '');
  }, [activeGroup]);

  const currentTeam = teams.find(t => t._id === activeTeam);
  const teamPlayers = players.filter(p => (currentTeam?.playerIds || []).includes(String(p._id)));

  const submit = async () => {
    if (!form.name.trim() || !activeTeam) return;
    await onAddPlayer(activeTeam, form);
    setForm({ name: '', email: '', phone: '' });
  };

  return (
    <div className="gm-step">
      <div className="gm-step-desc">
        Add players to each team. Players are tournament-specific — they won't appear in the global players database.
      </div>

      {/* Group selector */}
      <div className="gm-group-tabs">
        {groups.map(g => (
          <button key={g._id}
            className={`gm-group-tab${activeGroup === g._id ? ' active' : ''}`}
            onClick={() => setActiveGroup(g._id)}>
            {g.name}
          </button>
        ))}
      </div>

      {/* Team selector */}
      {teams.length > 0 && (
        <div className="gm-team-tabs">
          {teams.map(t => (
            <button key={t._id}
              className={`gm-team-tab${activeTeam === t._id ? ' active' : ''}`}
              onClick={() => setActiveTeam(t._id)}>
              <span className="gm-code-sm">{t.teamCode}</span>
              {t.teamName}
              <span className="gm-tab-count">{(t.playerIds?.length || 0)}</span>
            </button>
          ))}
        </div>
      )}

      {!activeTeam
        ? <div className="gm-empty">Select a team above to add players.</div>
        : (
          <>
            {/* Add player */}
            <div className="gm-add-bar gm-add-bar-3">
              <input className="gm-input gm-input-flex" placeholder="Player name *"
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && submit()} />
              <input className="gm-input gm-input-flex" placeholder="Email"
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <input className="gm-input gm-input-sm" placeholder="Phone"
                value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              <button className="gm-btn-primary" onClick={submit}>+ Add</button>
            </div>

            {/* Player list */}
            {teamPlayers.length === 0
              ? <div className="gm-empty">No players in {currentTeam?.teamName} yet.</div>
              : (
                <div className="gm-list">
                  {teamPlayers.map((p, idx) => (
                    <div key={p._id} className="gm-list-row">
                      <span className="gm-list-num">{idx + 1}</span>
                      <span className="gm-player-avatar">{p.name[0]?.toUpperCase()}</span>
                      <span className="gm-player-name">{p.name}</span>
                      <span className="gm-meta">{p.email || ''}</span>
                      <button className="gm-del-btn" onClick={() => onDeletePlayer(p._id)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
          </>
        )}

      <div className="gm-step-footer">
        <button className="gm-btn-ghost" onClick={onPrev}>← Back</button>
        <button className="gm-btn-next" onClick={onNext}>Next: Generate Matches →</button>
      </div>
    </div>
  );
}

// ─── STEP 4: Matches ───────────────────────────────────────────────────────────
function StepMatches({ groups, teamsMap, groupStage, fixtures, onGenerateFixtures, onRecordResult, onPrev, onNext }) {
  const [activeGroup, setActiveGroup] = useState(groups[0]?._id || '');
  const [scoreModal, setScoreModal] = useState(null); // fixture object
  const [scoreForm, setScoreForm] = useState({ sets: [{ p1: 0, p2: 0 }], status: 'live', winner: '', specialResult: '', date: '', time: '', court: '' });
  const [savingScore, setSavingScore] = useState(false);
  const [scoreError, setScoreError] = useState('');
  const [generating, setGenerating] = useState(false);

  const groupFixtures = fixtures[activeGroup] || [];
  const teams = teamsMap[activeGroup] || [];
  const groupObj = groups.find(g => g._id === activeGroup);
  const hasFixtures = groupFixtures.length > 0;

  const handleGenerate = async () => {
    setGenerating(true);
    await onGenerateFixtures(groupStage._id);
    setGenerating(false);
  };

  const openScoreModal = (fix) => {
    const m = fix.matchId;
    const now = new Date();
    const todayDate = now.toISOString().slice(0, 10);
    const currentTime = now.toTimeString().slice(0, 5);
    setScoreForm({
      sets: m?.score?.sets?.length > 0 ? m.score.sets.map(s => ({ ...s })) : [{ p1: 0, p2: 0 }],
      status: m?.status === 'completed' ? 'completed' : 'live',
      winner: m?.winner || '',
      specialResult: m?.specialResult || '',
      date: m?.date || todayDate,
      time: m?.time || currentTime,
      court: m?.court || '',
    });
    setScoreError('');
    setScoreModal(fix);
  };

  const addSet    = () => setScoreForm(p => ({ ...p, sets: [...p.sets, { p1: 0, p2: 0 }] }));
  const removeSet = () => setScoreForm(p => ({ ...p, sets: p.sets.slice(0, -1) }));
  const incSet    = (i, w) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: Math.min(99, (sets[i][w] || 0) + 1) }; setScoreForm(p => ({ ...p, sets })); };
  const decSet    = (i, w) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: Math.max(0, (sets[i][w] || 0) - 1) }; setScoreForm(p => ({ ...p, sets })); };
  const updSet    = (i, w, v) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: parseInt(v) || 0 }; setScoreForm(p => ({ ...p, sets })); };

  const saveScore = async () => {
    setScoreError('');
    if (scoreForm.status === 'completed' && !scoreForm.specialResult && !scoreForm.winner) {
      setScoreError('Please select a winner for completed match');
      return;
    }
    if (scoreForm.specialResult && !scoreForm.winner) {
      setScoreError('Please select a winner for the special result');
      return;
    }
    setSavingScore(true);
    try {
      await onRecordResult(scoreModal.matchId?._id, {
        score: { sets: scoreForm.sets },
        winner: scoreForm.winner || null,
        status: scoreForm.status,
        specialResult: scoreForm.specialResult || null,
      });
      setScoreModal(null);
    } catch (e) {
      setScoreError(e.message || 'Failed to save');
    } finally {
      setSavingScore(false);
    }
  };

  const statusColor = (s) => ({
    completed: 'gm-status-done',
    live:      'gm-status-live',
    scheduled: 'gm-status-pending',
  }[s] || 'gm-status-pending');

  return (
    <div className="gm-step">
      <div className="gm-step-desc">
        Round-robin matches are auto-generated — every team plays every other team once. Record results after each match.
      </div>

      {/* Group tabs */}
      <div className="gm-group-tabs">
        {groups.map(g => {
          const gf = fixtures[g._id] || [];
          const done = gf.filter(f => f.matchId?.status === 'completed').length;
          return (
            <button key={g._id}
              className={`gm-group-tab${activeGroup === g._id ? ' active' : ''}`}
              onClick={() => setActiveGroup(g._id)}>
              {g.name}
              <span className="gm-tab-count">{done}/{gf.length}</span>
            </button>
          );
        })}
      </div>

      {/* Generate button */}
      {!groupStage.fixturesGenerated && (
        <div className="gm-generate-bar">
          <div>
            <strong>No matches generated yet.</strong>
            <p className="gm-hint">This will create all round-robin matches for every group at once.</p>
          </div>
          <button className="gm-btn-primary" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating…' : '⚡ Generate All Matches'}
          </button>
        </div>
      )}

      {/* Match list */}
      {hasFixtures ? (
        <div className="gm-matches-list">
          {groupFixtures.map((fix, idx) => {
            const m = fix.matchId;
            const t1 = fix.team1Id;
            const t2 = fix.team2Id;
            const isDone = m?.status === 'completed';
            return (
              <div key={fix._id} className={`gm-match-card${isDone ? ' gm-match-done' : ''}`}>
                <span className="gm-match-num">M{idx + 1}</span>

                <div className="gm-match-teams">
                  <span className="gm-match-team">
                    <span className="gm-code-badge">{t1?.teamCode || '??'}</span>
                    {t1?.teamName || 'TBD'}
                  </span>
                  {isDone ? (
                    <div className="gm-score-display">
                      <span>{m?.score?.sets?.map(s => `${s.p1}-${s.p2}`).join(', ') || '—'}</span>
                    </div>
                  ) : (
                    <span className="gm-vs">vs</span>
                  )}
                  <span className="gm-match-team">
                    <span className="gm-code-badge">{t2?.teamCode || '??'}</span>
                    {t2?.teamName || 'TBD'}
                  </span>
                </div>

                <div className="gm-match-right">
                  {isDone && (
                    <span className="gm-winner-badge">
                      🏆 {fix.team1Id?._id === m?.winner ? t1?.teamCode : t2?.teamCode}
                    </span>
                  )}
                  <span className={`gm-status-pill ${statusColor(m?.status)}`}>
                    {m?.status || 'Pending'}
                  </span>
                  <button
                    className={`gm-score-btn${isDone ? ' gm-score-btn-edit' : ''}`}
                    onClick={() => openScoreModal(fix)}
                  >
                    {isDone ? 'Edit' : 'Enter Score'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : groupStage.fixturesGenerated ? (
        <div className="gm-empty">No matches in this group yet.</div>
      ) : null}

      <div className="gm-step-footer">
        <button className="gm-btn-ghost" onClick={onPrev}>← Back</button>
        <button className="gm-btn-next" onClick={onNext}>View Standings →</button>
      </div>

      {scoreModal && (() => {
        const t1 = scoreModal.team1Id;
        const t2 = scoreModal.team2Id;
        // Build a match-shaped object MatchUpdateForm expects
        const matchObj = {
          ...(scoreModal.matchId || {}),
          player1: scoreModal.team1Id?._id || scoreModal.team1Id,
          player2: scoreModal.team2Id?._id || scoreModal.team2Id,
          round: `Group Stage`,
        };
        return (
          <Modal isOpen onClose={() => setScoreModal(null)} title="Update Match">
            <MatchUpdateForm
              match={matchObj}
              t1={{ name: t1?.teamName || 'Team 1' }}
              t2={{ name: t2?.teamName || 'Team 2' }}
              scoreForm={scoreForm}
              setScoreForm={setScoreForm}
              addSet={addSet}
              removeSet={removeSet}
              decSet={decSet}
              incSet={incSet}
              updSet={updSet}
              saveScore={saveScore}
              savingScore={savingScore}
              scoreError={scoreError}
            />
          </Modal>
        );
      })()}
    </div>
  );
}

// ─── STEP 5: Standings ─────────────────────────────────────────────────────────
function StepStandings({ groups, teamsMap, standings, fixtures, onQualify, onPrev }) {
  const [activeGroup, setActiveGroup] = useState('all');

  const rows = standings[activeGroup] || [];
  const gFixtures = fixtures[activeGroup] || [];
  const totalMatches = gFixtures.length;
  const played = gFixtures.filter(f => f.matchId?.status === 'completed').length;
  const allDone = played === totalMatches && totalMatches > 0;

  // ── consolidated view across all groups ──────────────────────────────────────
  const AllGroupsView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {groups.map(g => {
        const gRows = standings[g._id] || [];
        const gFix = fixtures[g._id] || [];
        const gPlayed = gFix.filter(f => f.matchId?.status === 'completed').length;
        const gTotal = gFix.length;
        return (
          <div key={g._id} style={{ border: '1px solid var(--card-border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'var(--navy)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--lime)', fontFamily: 'var(--font-display)' }}>{g.name}</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{gPlayed}/{gTotal} played</span>
            </div>
            {gRows.length === 0
              ? <div className="gm-empty" style={{ padding: '12px 14px' }}>No results yet.</div>
              : (
                <table className="gm-standings-table" style={{ margin: 0 }}>
                  <thead>
                    <tr><th>#</th><th>Team</th><th>P</th><th>W</th><th>L</th><th>Pts</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {gRows.map((row, idx) => {
                      const team = (teamsMap[g._id] || []).find(t => String(t._id) === String(row.participantId));
                      return (
                        <tr key={idx} className={team?.status === 'qualified' ? 'gm-row-qualified' : ''}>
                          <td className="gm-pos">{idx + 1}</td>
                          <td className="gm-team-cell">
                            <span className="gm-code-badge">{team?.teamCode || '?'}</span>
                            {team?.teamName || row.participantId}
                          </td>
                          <td>{row.matchesPlayed}</td>
                          <td>{row.matchesWon}</td>
                          <td>{row.matchesLost}</td>
                          <td className="gm-pts">{row.matchPoints}</td>
                          <td>
                            {team?.status === 'qualified' && <span className="gm-qualify-badge">✅ Q</span>}
                            {team?.status === 'eliminated' && <span className="gm-elim-badge">❌</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="gm-step">
      <div className="gm-step-desc">
        Standings are calculated automatically from match results. Qualify the top teams to advance.
      </div>

      <div className="gm-group-tabs">
        <button
          className={`gm-group-tab${activeGroup === 'all' ? ' active' : ''}`}
          onClick={() => setActiveGroup('all')}>
          📊 All Groups
        </button>
        {groups.map(g => (
          <button key={g._id}
            className={`gm-group-tab${activeGroup === g._id ? ' active' : ''}`}
            onClick={() => setActiveGroup(g._id)}>
            {g.name}
          </button>
        ))}
      </div>

      {activeGroup === 'all'
        ? <AllGroupsView />
        : rows.length === 0
          ? <div className="gm-empty">Play some matches first to see standings.</div>
          : (
            <>
              <div className="gm-progress-bar">
                <span className="gm-hint">{played}/{totalMatches} matches played</span>
                <div className="gm-bar">
                  <div className="gm-bar-fill" style={{ width: `${totalMatches ? (played / totalMatches) * 100 : 0}%` }} />
                </div>
              </div>

              <table className="gm-standings-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Team</th>
                    <th>P</th>
                    <th>W</th>
                    <th>L</th>
                    <th>Pts</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const team = (teamsMap[activeGroup] || []).find(t =>
                      String(t._id) === String(row.participantId)
                    );
                    return (
                      <tr key={idx} className={team?.status === 'qualified' ? 'gm-row-qualified' : ''}>
                        <td className="gm-pos">{idx + 1}</td>
                        <td className="gm-team-cell">
                          <span className="gm-code-badge">{team?.teamCode || '?'}</span>
                          {team?.teamName || row.participantId}
                        </td>
                        <td>{row.matchesPlayed}</td>
                        <td>{row.matchesWon}</td>
                        <td>{row.matchesLost}</td>
                        <td className="gm-pts">{row.matchPoints}</td>
                        <td>
                          {team?.status === 'qualified' && <span className="gm-qualify-badge">✅ Qualified</span>}
                          {team?.status === 'eliminated' && <span className="gm-elim-badge">❌ Eliminated</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {allDone && (
                <div className="gm-qualify-bar">
                  <button className="gm-btn-primary" onClick={() => onQualify(activeGroup, 2)}>
                    ✅ Qualify Top 2
                  </button>
                  <button className="gm-btn-ghost" onClick={() => onQualify(activeGroup, 1)}>
                    Qualify Top 1
                  </button>
                </div>
              )}
            </>
          )}

      <div className="gm-step-footer">
        <button className="gm-btn-ghost" onClick={onPrev}>← Back</button>
      </div>
    </div>
  );
}

// ─── MAIN ──────────────────────────────────────────────────────────────────────
export default function GroupManager() {
  const { id: tournamentId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [tournament, setTournament] = useState(null);
  const [groupStage, setGroupStage] = useState(null);
  const [groups, setGroups] = useState([]);
  const [teamsMap, setTeamsMap] = useState({});
  const [players, setPlayers] = useState([]);
  const [standings, setStandings] = useState({});
  const [fixtures, setFixtures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── load ──────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const [tRes, gsRes] = await Promise.all([
        tpAPI.getTournament(tournamentId),
        tpAPI.getStageForTournament(tournamentId),
      ]);
      setTournament(tRes.data);

      if (!gsRes.data) { setLoading(false); return; }
      const gs = gsRes.data;
      setGroupStage(gs);
      const grps = gs.groups || [];
      setGroups(grps);

      const [teamsResults, plRes, standingsResults, fixturesResults] = await Promise.all([
        Promise.all(grps.map(g =>
          tpAPI.getTeams(g._id)
            .then(r => ({ gid: g._id, teams: r.data || [] }))
            .catch(() => ({ gid: g._id, teams: [] }))
        )),
        tpAPI.listPlayers(tournamentId).catch(() => ({ data: [] })),
        Promise.all(grps.map(g =>
          groupStageAPI.getGroupStandings(g._id)
            .then(r => ({ gid: g._id, rows: r.data || [] }))
            .catch(() => ({ gid: g._id, rows: [] }))
        )),
        Promise.all(grps.map(g =>
          groupStageAPI.getGroupFixtures(g._id)
            .then(r => ({ gid: g._id, fixtures: r.data || [] }))
            .catch(() => ({ gid: g._id, fixtures: [] }))
        )),
      ]);

      const tm = {}, st = {}, fx = {};
      teamsResults.forEach(({ gid, teams }) => { tm[gid] = teams; });
      standingsResults.forEach(({ gid, rows }) => { st[gid] = rows; });
      fixturesResults.forEach(({ gid, fixtures: f }) => { fx[gid] = f; });

      setTeamsMap(tm);
      setStandings(st);
      setFixtures(fx);
      setPlayers(plRes.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const fail  = (msg) => { setError(msg);   setTimeout(() => setError(''),   5000); };

  // ── actions ───────────────────────────────────────────────────────────────────
  const addGroup = async (name) => {
    try {
      await tpAPI.createGroup(groupStage._id, { name });
      flash(`Group "${name}" created`);
      await load();
    } catch (e) { fail(e.message); }
  };

  const renameGroup = async (gId, name) => {
    try {
      await tpAPI.renameGroup(gId, name);
      flash('Group renamed');
      await load();
    } catch (e) { fail(e.message); }
  };

  const deleteGroup = async (gId) => {
    try {
      await tpAPI.deleteGroup(gId);
      flash('Group deleted');
      await load();
    } catch (e) { fail(e.message); }
  };

  const addTeam = async (groupId, form) => {
    try {
      await tpAPI.createTeam(groupId, { ...form, playerIds: [] });
      flash(`Team "${form.teamName}" added`);
      await load();
    } catch (e) { fail(e.message); }
  };

  const renameTeam = async (teamId, data) => {
    try {
      await tpAPI.updateTeam(teamId, data);
      await load();
    } catch (e) { fail(e.message); }
  };

  const deleteTeam = async (teamId) => {
    try {
      await tpAPI.deleteTeam(teamId);
      flash('Team deleted');
      await load();
    } catch (e) { fail(e.message); }
  };

  const addPlayer = async (teamId, form) => {
    try {
      // 1. create player
      const res = await tpAPI.createPlayer(tournamentId, form);
      const playerId = res.data._id;
      // 2. attach to team
      const team = Object.values(teamsMap).flat().find(t => t._id === teamId);
      if (team) {
        await tpAPI.updateTeam(teamId, {
          teamName: team.teamName,
          teamCode: team.teamCode,
          playerIds: [...(team.playerIds || []), String(playerId)],
        });
      }
      flash(`Player "${form.name}" added`);
      await load();
    } catch (e) { fail(e.message); }
  };

  const deletePlayer = async (playerId) => {
    try {
      await tpAPI.deletePlayer(playerId);
      flash('Player removed');
      await load();
    } catch (e) { fail(e.message); }
  };

  const generateFixtures = async (gsId) => {
    try {
      await groupStageAPI.generateFixtures(gsId);
      flash('Matches generated!');
      await load();
    } catch (e) { fail(e.message); }
  };

  const recordResult = async (matchId, data) => {
    try {
      await groupStageAPI.recordResult(matchId, data);
      flash('Result saved');
      await load();
    } catch (e) { fail(e.message); }
  };

  const qualifyTeams = async (groupId, count) => {
    try {
      await tpAPI.qualifyTeams(groupId, count);
      flash(`Top ${count} team${count > 1 ? 's' : ''} qualified!`);
      await load();
    } catch (e) { fail(e.message); }
  };

  // ─────────────────────────────────────────────────────────────────────────────

  if (loading) return <div className="gm-loading">Loading…</div>;
  if (!groupStage) return (
    <div className="gm-no-stage">
      <p>No group stage found for this tournament.</p>
      <button className="gm-btn-ghost" onClick={() => navigate('/organizer/group-stages')}>← Back</button>
    </div>
  );

  return (
    <div className="gm-page">
      {/* ── top bar ── */}
      <div className="gm-topbar">
        <button className="back-btn" onClick={() => navigate('/organizer/group-stages')}>←</button>
        <div>
          <h1 className="heading">{tournament?.name?.toUpperCase()}</h1>
          <p className="sub-text">Group Stage Management</p>
        </div>
        <div className="gm-topbar-stats">
          <span>{groups.length} groups</span>
          <span>{Object.values(teamsMap).flat().length} teams</span>
          <span>{players.length} players</span>
        </div>
      </div>

      {/* ── alerts ── */}
      {error   && <div className="gm-alert gm-alert-err">{error}<button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="gm-alert gm-alert-ok">{success}<button onClick={() => setSuccess('')}>✕</button></div>}

      {/* ── step wizard ── */}
      <div className="gm-wizard">
        {/* step nav */}
        <div className="gm-steps-nav">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              className={`gm-step-btn${step === i ? ' active' : ''}${i < step ? ' done' : ''}`}
              onClick={() => setStep(i)}
            >
              <span className="gm-step-icon">{s.icon}</span>
              <span className="gm-step-label">{s.label}</span>
            </button>
          ))}
        </div>

        {/* step content */}
        <div className="gm-step-content">
          {step === 0 && <StepGroups groups={groups} groupStage={groupStage}
            onAdd={addGroup} onRename={renameGroup} onDelete={deleteGroup}
            onNext={() => setStep(1)} />}

          {step === 1 && <StepTeams groups={groups} teamsMap={teamsMap}
            onAddTeam={addTeam} onRenameTeam={renameTeam} onDeleteTeam={deleteTeam}
            onNext={() => setStep(2)} onPrev={() => setStep(0)} />}

          {step === 2 && <StepPlayers groups={groups} teamsMap={teamsMap} players={players}
            onAddPlayer={addPlayer} onDeletePlayer={deletePlayer}
            onNext={() => setStep(3)} onPrev={() => setStep(1)} />}

          {step === 3 && <StepMatches groups={groups} teamsMap={teamsMap}
            groupStage={groupStage} fixtures={fixtures}
            onGenerateFixtures={generateFixtures} onRecordResult={recordResult}
            onNext={() => setStep(4)} onPrev={() => setStep(2)} />}

          {step === 4 && <StepStandings groups={groups} teamsMap={teamsMap}
            standings={standings} fixtures={fixtures}
            onQualify={qualifyTeams} onPrev={() => setStep(3)} />}
        </div>
      </div>
    </div>
  );
}
